import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsApi } from '@/api/products';
import { modulesApi, Module } from '@/api/modules';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Save,
  Ticket,
  Package,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SelectedModule {
  code: string;
  name: string;
  isDefault: boolean;
  price: number | null;
  priceT2: number | null;
  priceT3: number | null;
  priceT4: number | null;
}

export function OfferFormPage() {
  const { productCode, offerCode } = useParams<{ productCode: string; offerCode: string }>();
  const navigate = useNavigate();
  const isEdit = !!offerCode;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableModules, setAvailableModules] = useState<Module[]>([]);
  const [productName, setProductName] = useState<string>('');

  // Form state
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [priceT1, setPriceT1] = useState<number | null>(null);
  const [priceT2, setPriceT2] = useState<number | null>(null);
  const [priceT3, setPriceT3] = useState<number | null>(null);
  const [priceT4, setPriceT4] = useState<number | null>(null);
  const [modules, setModules] = useState<SelectedModule[]>([]);

  // For adding new module
  const [selectedModuleCode, setSelectedModuleCode] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [productCode, offerCode]);

  const loadData = async () => {
    if (!productCode) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch product to get name
      const product = await productsApi.getByCode(productCode);
      setProductName(product.name);

      // Fetch V2 modules
      const allModules = await modulesApi.getAll();
      const v2Modules = allModules.filter(m => m.isV2);
      setAvailableModules(v2Modules);

      // If editing, fetch offer data
      if (isEdit && offerCode) {
        const offer = await productsApi.getOffer(productCode, offerCode);
        setName(offer.name);
        setCode(offer.code);
        setDescription(offer.description || '');
        setDisplayOrder(offer.displayOrder);
        setIsActive(offer.isActive);
        setPriceT1(offer.priceT1);
        setPriceT2(offer.priceT2);
        setPriceT3(offer.priceT3);
        setPriceT4(offer.priceT4);
        setModules(offer.modules.map(m => ({
          code: m.code,
          name: m.name,
          isDefault: m.isDefault,
          price: m.price,
          priceT2: m.priceT2,
          priceT3: m.priceT3,
          priceT4: m.priceT4,
        })));
      }
    } catch (err) {
      setError('Erreur lors du chargement des donnees');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productCode) return;
    if (!name.trim() || !code.trim()) {
      setError('Le nom et le code sont requis');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const data = {
        name,
        code,
        description: description || undefined,
        displayOrder,
        isActive,
        priceT1,
        priceT2,
        priceT3,
        priceT4,
        modules: modules.map(m => ({
          code: m.code,
          isDefault: m.isDefault,
          price: m.price,
          priceT2: m.priceT2,
          priceT3: m.priceT3,
          priceT4: m.priceT4,
        })),
      };

      if (isEdit && offerCode) {
        await productsApi.updateOffer(productCode, offerCode, data);
      } else {
        await productsApi.createOffer(productCode, data);
      }

      navigate(`/products/${productCode}`);
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAddModule = () => {
    if (!selectedModuleCode) return;

    const moduleToAdd = availableModules.find(m => m.code === selectedModuleCode);
    if (!moduleToAdd) return;

    // Check if already added
    if (modules.some(m => m.code === selectedModuleCode)) {
      setError('Ce module est deja ajoute');
      return;
    }

    setModules([...modules, {
      code: moduleToAdd.code,
      name: moduleToAdd.name,
      isDefault: true,
      price: null,
      priceT2: null,
      priceT3: null,
      priceT4: null,
    }]);
    setSelectedModuleCode('');
  };

  const handleRemoveModule = (code: string) => {
    setModules(modules.filter(m => m.code !== code));
  };

  const handleModuleChange = (
    code: string,
    changes: Partial<Omit<SelectedModule, 'code' | 'name'>>
  ) => {
    setModules(modules.map(m => (m.code === code ? { ...m, ...changes } : m)));
  };

  // Modules not yet added
  const availableToAdd = availableModules.filter(
    m => !modules.some(added => added.code === m.code)
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/products" className="hover:text-foreground">Produits</Link>
        <span>/</span>
        <Link to={`/products/${productCode}`} className="hover:text-foreground">{productName}</Link>
        <span>/</span>
        <span className="text-foreground">{isEdit ? `Modifier ${name}` : 'Nouvelle offre'}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" type="button" asChild>
              <Link to={`/products/${productCode}`}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Ticket className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              {isEdit ? `Modifier l'offre ${name}` : 'Nouvelle offre'}
            </h1>
          </div>
        </div>

        <Button type="submit" disabled={saving}>
          <Save className="h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* General Info */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="text-base">Informations generales</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="MVP"
                disabled={isEdit}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Offre MVP"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description de l'offre..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="displayOrder">Ordre d'affichage</Label>
              <Input
                id="displayOrder"
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                min={0}
              />
            </div>
            <div className="flex items-center justify-between space-x-2 pt-6">
              <Label htmlFor="isActive">Offre active</Label>
              <Switch
                id="isActive"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="flex items-center gap-2 text-base">
            <Ticket className="h-4 w-4 text-primary" />
            Tarification par tranche
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priceT1">
                <Badge variant="outline" className="font-mono mr-2">T1</Badge>
                0 - 100 élèves
              </Label>
              <Input
                id="priceT1"
                type="number"
                step="0.01"
                min="0"
                value={priceT1 ?? ''}
                onChange={(e) => setPriceT1(e.target.value === '' ? null : parseFloat(e.target.value))}
                className="font-mono"
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priceT2">
                <Badge variant="outline" className="font-mono mr-2">T2</Badge>
                101 - 250 élèves
              </Label>
              <Input
                id="priceT2"
                type="number"
                step="0.01"
                min="0"
                value={priceT2 ?? ''}
                onChange={(e) => setPriceT2(e.target.value === '' ? null : parseFloat(e.target.value))}
                className="font-mono"
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priceT3">
                <Badge variant="outline" className="font-mono mr-2">T3</Badge>
                251 - 500 élèves
              </Label>
              <Input
                id="priceT3"
                type="number"
                step="0.01"
                min="0"
                value={priceT3 ?? ''}
                onChange={(e) => setPriceT3(e.target.value === '' ? null : parseFloat(e.target.value))}
                className="font-mono"
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priceT4">
                <Badge variant="outline" className="font-mono mr-2">T4</Badge>
                501+ élèves
              </Label>
              <Input
                id="priceT4"
                type="number"
                step="0.01"
                min="0"
                value={priceT4 ?? ''}
                onChange={(e) => setPriceT4(e.target.value === '' ? null : parseFloat(e.target.value))}
                className="font-mono"
                placeholder="0.00"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modules */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4 text-primary" />
            Modules ({modules.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          {/* Add module */}
          <div className="flex gap-2">
            <Select value={selectedModuleCode} onValueChange={setSelectedModuleCode}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Selectionner un module a ajouter..." />
              </SelectTrigger>
              <SelectContent>
                {availableToAdd.map((module) => (
                  <SelectItem key={module.code} value={module.code}>
                    {module.name} ({module.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              onClick={handleAddModule}
              disabled={!selectedModuleCode}
            >
              <Plus className="h-4 w-4" />
              Ajouter
            </Button>
          </div>

          {/* Modules list */}
          {modules.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">
              Aucun module ajoute. Selectionnez des modules ci-dessus.
            </p>
          ) : (
            <div className="space-y-2">
              <div className="hidden md:grid md:grid-cols-[1fr_auto_repeat(4,6rem)_2.5rem] gap-2 items-center px-3 text-xs text-muted-foreground">
                <span>Module</span>
                <span className="w-24">Par défaut</span>
                <span className="text-right">T1</span>
                <span className="text-right">T2</span>
                <span className="text-right">T3</span>
                <span className="text-right">T4</span>
                <span />
              </div>
              {modules.map((module) => (
                <div
                  key={module.code}
                  className="grid grid-cols-1 md:grid-cols-[1fr_auto_repeat(4,6rem)_2.5rem] gap-2 items-center px-3 py-2 rounded-lg border bg-muted/20"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium text-sm truncate">{module.name}</span>
                  </div>
                  <div className="flex items-center gap-2 w-24">
                    <Switch
                      checked={module.isDefault}
                      onCheckedChange={(checked) =>
                        handleModuleChange(module.code, { isDefault: checked })
                      }
                    />
                    {!module.isDefault && (
                      <Badge variant="outline" className="text-xs">Au choix</Badge>
                    )}
                  </div>
                  {(['price', 'priceT2', 'priceT3', 'priceT4'] as const).map((field) => (
                    <Input
                      key={field}
                      type="number"
                      step="0.01"
                      min="0"
                      value={module[field] ?? ''}
                      onChange={(e) =>
                        handleModuleChange(module.code, {
                          [field]: e.target.value === '' ? null : parseFloat(e.target.value),
                        })
                      }
                      className="font-mono h-8 text-right"
                      placeholder="—"
                    />
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => handleRemoveModule(module.code)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 h-6 w-6"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <p className="text-xs text-muted-foreground px-3">
                Prix HT / élève / an par tranche d'effectif. Les modules "au choix" ne sont pas
                inclus par défaut : ils sont sélectionnables école par école (offre sur mesure).
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </form>
  );
}
